import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../context/authcontext";
import {
 
  FaShieldAlt,
  FaUserCog,
  FaUniversity,
  FaPhoneAlt,
  FaAngleLeft,
  FaAngleRight,
  FaFolderOpen,
} from "react-icons/fa";

import {
  getProfile,
  changePassword,
  updateProfile,
  getMyEmployeeProfile,
  updateMyEmployeeProfile,
} from "../services/profileservice";

import PersonalInfo from "./personal_info";
import BankAccount from "./bank_account";
import EmergencyContact from "./emergency_contact";
import MyDocuments from "./my_documents";
import SecuritySettings from "./change_password";

import "./profile.css";

/* =========================================================
   TAB REGISTRY
========================================================= */

const TABS = [
  {
    key: "personal",
    label: "Personal Info",
    icon: FaUserCog,
  },

  {
    key: "bank",
    label: "Bank Account",
    icon: FaUniversity,
    permissions: ["employee.view_bank_details"],
  },

  {
    key: "emergency",
    label: "Emergency Contact",
    icon: FaPhoneAlt,
    permissions: ["employee.view_personal_details"],
  },

  {
    key: "documents",
    label: "My Documents",
    icon: FaFolderOpen,
    permissions: [
      "document.view",
      "employee.view_documents",
      "employee.view_sensitive_documents",
      "employee.upload_self_document",
    ],
  },

  {
    key: "security",
    label: "Security",
    icon: FaShieldAlt,
  },
];

export default function Profile() {
  const { hasAnyPermission } = useAuth();
  const location = useLocation();

  /* =========================================================
     STATE
  ========================================================= */

  const [activeSection, setActiveSection] = useState("personal");

  const [loading, setLoading] = useState(true);

  const [savingPhoto, setSavingPhoto] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [savingEmergency, setSavingEmergency] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [personal, setPersonal] = useState({});

  const [passwords, setPasswords] = useState({
    current: "",
    newPass: "",
    confirm: "",
  });

  const [documents, setDocuments] = useState([]);

  /* =========================================================
     VISIBLE TABS
  ========================================================= */

  const visibleTabs = TABS.filter(
    (tab) =>
      !tab.permissions ||
      hasAnyPermission(tab.permissions)
  );

  /* =========================================================
     TAB REFERENCES
  ========================================================= */

  const tabRefs = useRef({});
  const navRef = useRef(null);

  const [indicator, setIndicator] = useState({
    left: 0,
    width: 0,
    ready: false,
  });

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  /* =========================================================
     SLIDING INDICATOR
  ========================================================= */

  const measureIndicator = useCallback(() => {
    const nav = navRef.current;
    const el = tabRefs.current[activeSection];

    if (!nav || !el) return;

    setIndicator({
      left: el.offsetLeft,
      width: el.offsetWidth,
      ready: true,
    });
  }, [activeSection]);

  /* =========================================================
     SCROLL ARROW VISIBILITY (works for any number of tabs)
  ========================================================= */

  const updateArrowVisibility = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;

    setShowLeftArrow(nav.scrollLeft > 4);
    setShowRightArrow(
      nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 4
    );
  }, []);

  const scrollNav = (dir) => {
    const nav = navRef.current;
    if (!nav) return;
    nav.scrollBy({ left: dir * 160, behavior: "smooth" });
  };

  /* =========================================================
     INITIAL TAB MEASUREMENT
  ========================================================= */

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      measureIndicator();
      updateArrowVisibility();
    });

    return () => cancelAnimationFrame(frame);
  }, [
    measureIndicator,
    updateArrowVisibility,
    loading,
    visibleTabs.length,
  ]);

  /* =========================================================
     WINDOW RESIZE
  ========================================================= */

  useEffect(() => {
    const handleResize = () => {
      measureIndicator();
      updateArrowVisibility();
    };

    window.addEventListener("resize", handleResize);

    return () =>
      window.removeEventListener("resize", handleResize);
  }, [measureIndicator, updateArrowVisibility]);

  /* =========================================================
     ENSURE ACTIVE TAB IS VISIBLE
  ========================================================= */

  useEffect(() => {
    const nav = navRef.current;
    const activeTab = tabRefs.current[activeSection];

    if (!nav || !activeTab) return;

    const navRect = nav.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    if (tabRect.right > navRect.right) {
      nav.scrollBy({
        left: tabRect.right - navRect.right + 20,
        behavior: "smooth",
      });
    }

    if (tabRect.left < navRect.left) {
      nav.scrollBy({
        left: tabRect.left - navRect.left - 20,
        behavior: "smooth",
      });
    }

    const timeout = setTimeout(() => {
      measureIndicator();
      updateArrowVisibility();
    }, 350);

    return () => clearTimeout(timeout);
  }, [activeSection, measureIndicator, updateArrowVisibility]);

  /* =========================================================
     LOAD PROFILE
  ========================================================= */

  const fetchProfile = async () => {
    try {
      const res = await getProfile();

      const data = res.data || res;

      let empData = {};

      try {
        const empRes = await getMyEmployeeProfile();

        empData = empRes.data || empRes || {};
      } catch (e) {
        /*
          Employee profile may not exist
          for every account.
        */
      }

      setPersonal({
        first_name: data.first_name || "",

        last_name: data.last_name || "",

        email: data.email || "",

        phone: data.phone || "",

        role:
          (typeof data.role === "object"
            ? data.role?.name
            : data.role) ||
          (Array.isArray(data.roles) && data.roles[0]
            ? typeof data.roles[0] === "object"
              ? data.roles[0]?.name
              : data.roles[0]
            : "") ||
          "",

        photoPreview: data.profile_photo_url || null,

        employee_id:
          empData.emp_id ||
          data.employee_details?.employee_id ||
          "",

        designation:
          (typeof empData.designation === "object"
            ? empData.designation?.name
            : empData.designation) ||
          (typeof data.employee_details?.designation === "object"
            ? data.employee_details?.designation?.name
            : data.employee_details?.designation) ||
          "",

        department:
          (typeof empData.department === "object"
            ? empData.department?.name
            : empData.department) ||
          (typeof data.employee_details?.department === "object"
            ? data.employee_details?.department?.name
            : data.employee_details?.department) ||
          "",

        gender:
          empData.gender || data.employee_details?.gender || "",

        date_of_birth:
          empData.date_of_birth ||
          data.employee_details?.date_of_birth ||
          "",

        birthday:
          empData.birthday || data.employee_details?.birthday || "",

        joining_date:
          empData.date_of_joining ||
          data.employee_details?.joining_date ||
          "",

        reporting_manager:
          (typeof empData.reporting_manager === "object"
            ? empData.reporting_manager?.full_name ||
              empData.reporting_manager?.name ||
              empData.reporting_manager?.emp_id
            : empData.reporting_manager) ||
          (typeof data.employee_details?.reporting_manager === "object"
            ? data.employee_details?.reporting_manager?.full_name ||
              data.employee_details?.reporting_manager?.name ||
              data.employee_details?.reporting_manager?.emp_id
            : data.employee_details?.reporting_manager) ||
          "",

        bank_account_number: empData.bank_account_number || "",

        ifsc_code: empData.ifsc_code || "",

        bank_name: empData.bank_name || "",

        emergency_contact_name: empData.emergency_contact_name || "",

        emergency_contact_number:
          empData.emergency_contact_number || "",
      });

      setDocuments(empData.documents || []);
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL PROFILE LOAD
  ========================================================= */

  useEffect(() => {
    fetchProfile();
  }, []);

  /* =========================================================
     DEEP LINK
     /profile?tab=bank | emergency | documents | security
  ========================================================= */

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const tab = params.get("tab");

    if (TABS.some((item) => item.key === tab)) {
      setActiveSection(tab);
    }
  }, [location.search]);

  /* =========================================================
     SAVE PROFILE PHOTO ONLY
     (Basic Info itself is read-only / constant)
  ========================================================= */

  const savePhoto = async (file) => {
    setSavingPhoto(true);

    try {
      const formData = new FormData();
      formData.append("profile_photo", file);

      await updateProfile(formData);

      toast.success("Profile photo updated");

      await fetchProfile();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update photo"
      );
    } finally {
      setSavingPhoto(false);
    }
  };

  /* =========================================================
     SAVE BANK DETAILS
  ========================================================= */

  const saveBankDetails = async (fields) => {
    setSavingBank(true);

    try {
      await updateMyEmployeeProfile({
        bank_account_number: fields.bank_account_number || "",
        ifsc_code: fields.ifsc_code || "",
        bank_name: fields.bank_name || "",
      });

      toast.success("Bank details saved");

      await fetchProfile();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to save bank details"
      );
    } finally {
      setSavingBank(false);
    }
  };

  /* =========================================================
     SAVE EMERGENCY CONTACT
  ========================================================= */

  const saveEmergencyContact = async (fields) => {
    setSavingEmergency(true);

    try {
      await updateMyEmployeeProfile({
        emergency_contact_name: fields.emergency_contact_name || "",
        emergency_contact_number:
          fields.emergency_contact_number || "",
      });

      toast.success("Emergency contact saved");

      await fetchProfile();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Failed to save emergency contact"
      );
    } finally {
      setSavingEmergency(false);
    }
  };

  /* =========================================================
     CHANGE PASSWORD
  ========================================================= */

  const savePassword = async () => {
    setSavingPassword(true);

    try {
      await changePassword({
        current_password: passwords.current,

        new_password: passwords.newPass,

        confirm_password: passwords.confirm,
      });

      toast.success("Password updated successfully");

      setPasswords({
        current: "",
        newPass: "",
        confirm: "",
      });
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Password update failed"
      );
    } finally {
      setSavingPassword(false);
    }
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="account-loading">
        <div className="account-loading-spinner" />

        <p>Loading profile…</p>
      </div>
    );
  }

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="account-root">
      {/* ================= HEADER ================= */}

      <div className="account-header">
        <h2>Profile Settings</h2>

        <p>Manage your account information and preferences</p>
      </div>

      {/* ================= TABS ================= */}

      <div className="account-nav-shell">
        {showLeftArrow && (
          <button
            type="button"
            className="account-nav-arrow account-nav-arrow-left"
            onClick={() => scrollNav(-1)}
            aria-label="Scroll tabs left"
          >
            <FaAngleLeft />
          </button>
        )}

        <div
          className="account-nav"
          ref={navRef}
          onScroll={updateArrowVisibility}
        >
          {indicator.ready && (
            <span
              className="account-nav-indicator"
              style={{
                left: indicator.left,
                width: indicator.width,
              }}
            />
          )}

          {visibleTabs.map((tab) => {
            const Icon = tab.icon;

            return (
              <button
                key={tab.key}
                ref={(el) => {
                  tabRefs.current[tab.key] = el;
                }}
                className={`account-nav-btn ${
                  activeSection === tab.key ? "active" : ""
                }`}
                onClick={() => setActiveSection(tab.key)}
                type="button"
              >
                <Icon />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {showRightArrow && (
          <button
            type="button"
            className="account-nav-arrow account-nav-arrow-right"
            onClick={() => scrollNav(1)}
            aria-label="Scroll tabs right"
          >
            <FaAngleRight />
          </button>
        )}
      </div>

      {/* ================= CONTENT ================= */}

      <div className="account-content" key={activeSection}>
        {activeSection === "personal" && (
          <PersonalInfo
            personal={personal}
            onUploadPhoto={savePhoto}
            uploadingPhoto={savingPhoto}
          />
        )}

        {activeSection === "bank" && (
          <BankAccount
            personal={personal}
            onSave={saveBankDetails}
            saving={savingBank}
          />
        )}

        {activeSection === "emergency" && (
          <EmergencyContact
            personal={personal}
            onSave={saveEmergencyContact}
            saving={savingEmergency}
          />
        )}

        {activeSection === "documents" &&
          hasAnyPermission([
            "document.view",
            "employee.view_documents",
            "employee.view_sensitive_documents",
            "employee.upload_self_document",
          ]) && (
            <MyDocuments documents={documents} onRefresh={fetchProfile} />
          )}

        {activeSection === "security" && (
          <SecuritySettings
            passwords={passwords}
            setPasswords={setPasswords}
            onSavePassword={savePassword}
            savingPassword={savingPassword}
          />
        )}
      </div>
    </div>
  );
}